import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiClock, FiSmartphone } from 'react-icons/fi';
import { HiOutlineUserGroup } from 'react-icons/hi2';
import { MdOutlineVerified, MdOutlineStorefront } from 'react-icons/md';
import methodModel from '../../../methods/methods';

/* ─── Colorful palette for avatar gradients ─── */
const PALETTE = [
  ['#667eea', '#764ba2'], ['#f093fb', '#f5576c'], ['#4facfe', '#00f2fe'],
  ['#43e97b', '#38f9d7'], ['#fa709a', '#fee140'], ['#a18cd1', '#fbc2eb'],
  ['#fccb90', '#d57eeb'], ['#30cfd0', '#667eea'], ['#fd746c', '#ff9068'],
  ['#96fbc4', '#f9f586'], ['#89f7fe', '#66a6ff'], ['#f5af19', '#f12711'],
];

const getGradient = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
};

const getInitials = (name = '') => {
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase() || '?';
};

/* ═══════════════════════════════════════════════════
   SingleAvatar — extracted component (Rules of Hooks)
═══════════════════════════════════════════════════ */
const SingleAvatar = ({ user, index, size, maxShow }) => {
  const [err, setErr] = useState(false);
  const colors = getGradient(user?.fullName || user?.name || String(index));
  const name   = user?.fullName || user?.name || 'User';
  const img    = user?.image || user?.profileImage || user?.avatar;
  const src    = img ? methodModel.userImg(img) : null;

  return (
    <div
      title={name}
      style={{
        width: size, height: size, borderRadius: '50%',
        border: '2px solid #fff',
        marginLeft: index === 0 ? 0 : -(size * 0.3),
        zIndex: maxShow - index,
        position: 'relative', flexShrink: 0, overflow: 'hidden',
        background: `linear-gradient(135deg,${colors[0]},${colors[1]})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        animationName: 'vvpPop',
        animationDuration: '0.38s',
        animationTimingFunction: 'cubic-bezier(0.175,0.885,0.32,1.275)',
        animationDelay: `${index * 50}ms`,
        animationFillMode: 'both',
      }}
    >
      {src && !err
        ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} />
        : <span style={{ fontSize: size * 0.36, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{getInitials(name)}</span>
      }
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   StackedAvatarGroup — exported for use in table
═══════════════════════════════════════════════════ */
export const StackedAvatarGroup = ({ users = [], totalCount = 0, size = 28, maxShow = 4 }) => {
  const shown = users.slice(0, maxShow);
  const extra = Math.max(0, totalCount - shown.length);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((user, i) => (
        <SingleAvatar key={user?.id || user?._id || i} user={user} index={i} size={size} maxShow={maxShow} />
      ))}
      {extra > 0 && (
        <div style={{
          width: size, height: size, borderRadius: '50%',
          border: '2px solid #fff',
          marginLeft: -(size * 0.3),
          zIndex: 0, flexShrink: 0,
          background: 'linear-gradient(135deg,#92400E,#CC9900)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size * 0.3, fontWeight: 800, color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        }}>
          +{extra > 99 ? '99' : extra}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   Format seconds to human-readable string
   ═══════════════════════════════════════════════════ */
const formatSeconds = (secs) => {
  if (secs === undefined || secs === null || secs === '') return '';
  const s = Number(secs);
  if (isNaN(s) || s <= 0) return '';
  
  if (s < 60) {
    return `${s} ${s === 1 ? 'sec' : 'secs'}`;
  }
  
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = s % 60;
    const minStr = `${m} ${m === 1 ? 'min' : 'mins'}`;
    if (rem > 0) {
      return `${minStr} ${rem} ${rem === 1 ? 'sec' : 'secs'}`;
    }
    return minStr;
  }
  
  const h = Math.floor(m / 60);
  const remM = m % 60;
  const hrStr = `${h} ${h === 1 ? 'hr' : 'hrs'}`;
  if (remM > 0) {
    return `${hrStr} ${remM} ${remM === 1 ? 'min' : 'mins'}`;
  }
  return hrStr;
};

/* ═══════════════════════════════════════════════════
   UserProfileCard
   ═══════════════════════════════════════════════════ */
const UserProfileCard = ({ user, index }) => {
  const [err, setErr] = useState(false);
  const colors        = getGradient(user?.fullName || user?.name || String(index));
  const name          = user?.fullName || user?.name || 'Anonymous User';
  const img           = user?.image || user?.profileImage || user?.avatar;
  const src           = img ? methodModel.userImg(img) : null;
  const email         = user?.email || '';
  const platform      = user?.platform || user?.signupPlatform || '';
  const lastVisit     = user?.lastVisit || user?.lastSeen || user?.updatedAt || '';
  const loyaltyPts    = user?.loyaltyPoints || user?.points || 0;
  const isVerified    = user?.isVerified || user?.emailVerified || false;
  const timeSpentReadable = user?.timeSpent !== undefined ? formatSeconds(user.timeSpent) : '';

  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1.5px solid #F3F4F6',
        borderRadius: 16,
        padding: '14px',
        display: 'flex', flexDirection: 'column', gap: 10,
        transition: 'all 0.2s ease',
        animationName: 'vvpSlide',
        animationDuration: '0.4s',
        animationTimingFunction: 'cubic-bezier(0.175,0.885,0.32,1.275)',
        animationDelay: `${index * 40}ms`,
        animationFillMode: 'both',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(204,153,0,0.13)';
        e.currentTarget.style.borderColor = 'rgba(204,153,0,0.3)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
        e.currentTarget.style.borderColor = '#F3F4F6';
      }}
    >
      {/* Avatar row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          flexShrink: 0, overflow: 'hidden',
          background: `linear-gradient(135deg,${colors[0]},${colors[1]})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 3px 10px ${colors[0]}50`,
          border: '2px solid rgba(255,255,255,0.9)',
        }}>
          {src && !err
            ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} />
            : <span style={{ fontSize: 15, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{getInitials(name)}</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <p style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{name}</p>
            {isVerified && <MdOutlineVerified style={{ color: '#3B82F6', fontSize: 13, flexShrink: 0 }} />}
          </div>
          {email && <p style={{ fontSize: 11, color: '#9CA3AF', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</p>}
        </div>
      </div>


      {/* Stats row — loyalty points and time spent */}
      {(loyaltyPts > 0 || timeSpentReadable) && (
        <div style={{ display: 'flex', gap: 6 }}>
          {loyaltyPts > 0 && (
            <div style={{
              flex: 1, borderRadius: 10, padding: '7px 8px',
              background: 'linear-gradient(135deg, rgba(204, 153, 0, 0.08), rgba(204, 153, 0, 0.02))',
              border: '1px solid rgba(204, 153, 0, 0.2)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            }}>
              <span style={{ fontSize: 16, fontWeight: 900, color: '#CC9900', lineHeight: 1 }}>{loyaltyPts}</span>
              <span style={{ fontSize: 9, color: '#92400E', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Points</span>
            </div>
          )}
          {timeSpentReadable && (
            <div style={{
              flex: 1, borderRadius: 10, padding: '7px 8px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02))',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#3B82F6', lineHeight: 1.2, textAlign: 'center', whiteSpace: 'nowrap' }}>{timeSpentReadable}</span>
              <span style={{ fontSize: 9, color: '#1E3A8A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Spent Time</span>
            </div>
          )}
        </div>
      )}

      {/* Meta */}
      {(lastVisit || platform) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 6, borderTop: '1px solid #F9FAFB' }}>
          {lastVisit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <FiClock style={{ color: '#D1D5DB', fontSize: 10, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: '#9CA3AF' }}>Last: <strong style={{ color: '#6B7280' }}>{fmtDate(lastVisit)}</strong></span>
            </div>
          )}
          {platform && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <FiSmartphone style={{ color: '#D1D5DB', fontSize: 10, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: '#6B7280', textTransform: 'capitalize', fontWeight: 500 }}>{platform}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   VenueVisitorsPanel — rendered via portal to <body>
   so it's never clipped by any ancestor's overflow
═══════════════════════════════════════════════════ */
const VenueVisitorsPanel = ({ venue, onClose }) => {
  const users       = venue?.users || [];
  const venueName   = venue?.name || 'Venue';
  const venueCat    = venue?.category || '';
  const venueTotal  = venue?.totalUsers || users.length || 0;

  /* Escape key */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  /* Lock body scroll while open */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const content = (
    <>
      <style>{`
        @keyframes vvpPop {
          from { opacity:0; transform:scale(0.5) translateY(6px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes vvpSlide {
          from { opacity:0; transform:translateY(18px) scale(0.96); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes vvpPanelIn {
          from { opacity:0; transform:translate(-50%,-50%) scale(0.93); }
          to   { opacity:1; transform:translate(-50%,-50%) scale(1); }
        }
        @keyframes vvpBgIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        .vvp-scroll::-webkit-scrollbar { width:4px; }
        .vvp-scroll::-webkit-scrollbar-track { background:transparent; }
        .vvp-scroll::-webkit-scrollbar-thumb { background:#E5E7EB; border-radius:99px; }
        .vvp-scroll::-webkit-scrollbar-thumb:hover { background:rgba(204,153,0,0.35); }
      `}</style>

      {/* ── Full-screen backdrop ── */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(10,12,20,0.6)',
          backdropFilter: 'blur(5px)',
          animationName: 'vvpBgIn', animationDuration: '0.25s', animationFillMode: 'both',
        }}
      />

      {/* ── Modal ── */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 9999,
          width: 'min(94vw, 860px)',
          maxHeight: '90vh',
          background: '#F9FAFB',
          borderRadius: 24,
          boxShadow: '0 32px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.07)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animationName: 'vvpPanelIn', animationDuration: '0.35s',
          animationTimingFunction: 'cubic-bezier(0.175,0.885,0.32,1.275)',
          animationFillMode: 'both',
        }}
      >
        {/* ── HEADER — matches TavLoy gold theme ── */}
        <div style={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFDF7 60%, #FFF9EC 100%)',
          padding: '22px 24px 18px',
          position: 'relative', flexShrink: 0,
          borderBottom: '1px solid rgba(204,153,0,0.2)',
        }}>
          {/* Gold accent line */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #CC9900, #F0C040, #CC9900)', borderRadius: '24px 24px 0 0' }} />

          {/* Decorative glow */}
          <div style={{ position: 'absolute', top: -40, right: 60, width: 140, height: 140, borderRadius: '50%', background: 'rgba(204,153,0,0.1)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -50, left: 40, width: 100, height: 100, borderRadius: '50%', background: 'rgba(204,153,0,0.04)', pointerEvents: 'none' }} />

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 16, right: 16,
              width: 32, height: 32, borderRadius: '50%',
              background: '#FFFFFF',
              border: '1px solid #E4E7EC',
              color: '#667085', fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', zIndex: 2,
              boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D0D5DD'; e.currentTarget.style.color = '#101828'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#E4E7EC'; e.currentTarget.style.color = '#667085'; }}
          >
            <FiX />
          </button>

          {/* Venue identity */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Logo / initial */}
            {venue?.logo ? (
              <img
                src={methodModel.userImg(venue.logo)}
                alt={venueName}
                style={{ width: 52, height: 52, borderRadius: 14, objectFit: 'cover', border: '2px solid rgba(204,153,0,0.6)', flexShrink: 0 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: 'linear-gradient(135deg,#CC9900,#F0C040)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 900, color: '#FFFFFF',
                border: '2px solid rgba(204,153,0,0.6)',
                boxShadow: '0 4px 14px rgba(204,153,0,0.3)',
              }}>
                {venueName.charAt(0)}
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Name + category */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 19, fontWeight: 900, color: '#111827', margin: 0, lineHeight: 1.2, textTransform: 'capitalize' }}>{venueName}</h2>
                {venueCat && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#B45309',
                    background: '#FFF',
                    border: '1px solid rgba(204,153,0,0.4)',
                    padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize',
                  }}>{venueCat}</span>
                )}
              </div>

              {/* Stats strip */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HiOutlineUserGroup style={{ color: '#CC9900', fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: '#4B5563', fontWeight: 500 }}>
                    <strong style={{ color: '#111827', fontWeight: 800 }}>{venueTotal.toLocaleString()}</strong> total visitors
                  </span>
                </div>
                {users.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdOutlineStorefront style={{ color: '#854D0E', fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: '#4B5563' }}>
                      <strong style={{ color: '#111827' }}>{users.length}</strong> profile{users.length !== 1 ? 's' : ''} shown
                    </span>
                  </div>
                )}
                {venue?.status && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 6px rgba(16,185,129,0.5)' }} />
                    <span style={{ fontSize: 11, color: '#065F46', fontWeight: 600 }}>{venue.status}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section label */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(204,153,0,0.2)' }}>
            <p style={{ fontSize: 10, color: '#78350F', margin: 0, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              👥 Visitor Profiles
            </p>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div
          className="vvp-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}
        >
          {users.length === 0 ? (
            /* ── Empty state ── */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 14 }}>
              <div style={{
                width: 72, height: 72, borderRadius: 22,
                background: 'linear-gradient(135deg,rgba(204,153,0,0.1),rgba(204,153,0,0.05))',
                border: '1.5px solid rgba(204,153,0,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
              }}>
                👥
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontWeight: 800, fontSize: 16, color: '#374151', margin: 0 }}>No visitor profiles yet</p>
                <p style={{ fontSize: 12, color: '#9CA3AF', margin: '6px 0 0', lineHeight: 1.6 }}>Visitor profiles will appear here once users check in.</p>
              </div>
              <div style={{
                padding: '12px 18px', borderRadius: 14,
                background: '#fff', border: '1.5px solid #F0F0F0',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              }}>
                <HiOutlineUserGroup style={{ color: '#CC9900', fontSize: 20 }} />
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: 0 }}>{venueTotal.toLocaleString()} users tracked</p>
                  <p style={{ fontSize: 11, color: '#9CA3AF', margin: 0 }}>Aggregate visitor count for this venue</p>
                </div>
              </div>
            </div>
          ) : (
            /* ── Profile grid ── */
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 14,
            }}>
              {users.map((user, i) => (
                <UserProfileCard
                  key={user?.id || user?._id || user?.userId || i}
                  user={user}
                  index={i}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  /* ── Render into <body> so no ancestor clip applies ── */
  return ReactDOM.createPortal(content, document.body);
};

export default VenueVisitorsPanel;
